/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SignInPage
 * --------
 * This is the sign-in page of the application responsible for authenticating users.
 *
 * Responsibilities:
 *  - Presents a sign-in form through the reusable SignInForm component.
 *  - Manages login requests by sending user credentials (username, password) along with 
 *    application ID to the backend authentication API endpoint.
 *  - Handles API responses and error conditions, displaying appropriate error messages.
 *  - Stores authenticated user data securely upon successful login using a storage helper.
 *  - Redirects authenticated users to the dashboard page.
 *  - Conditionally displays the option for user registration based on environment configuration.
 *
 * Key integrations:
 *  - Uses APIHelper instance configured for backend API communication.
 *  - Utilizes react-router's useNavigate hook for programmatic navigation.
 *  - Leverages environment variables for API URLs, client credentials, and app configuration.
 *
 * Usage:
 *  The SignInPage is used as a route page in the application's routing setup.
 *
 * Example:
 *  <Route path="/sign-in" element={<SignInPage />} />
 *
 * Note:
 *  - The SignInForm component must accept `onSubmit` and `showSignUp` props for handling form submission and conditional rendering of sign-up links respectively.
 */


import React, { useState } from "react";
import SignInForm, { buildSsoAuthorizeUrl } from "../components/SignInForm";
import { stashSsoNext } from "../utils/returnTo";
import PublicErrorPage from "../components/public/PublicErrorPage";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { store, retrieve } from "../utils/storage_helper";
import { isUserLoggedIn } from "../utils/utils";
import AlertDialog from "../components/common/AlertDialog";
import RoleChoiceDialog from "../components/organizer/RoleChoiceDialog";
import {
    isOrganizerAdmin,
    storeRoles,
    ROLE_CHOICE_ORGANIZER,
} from "../utils/roles";
import { getProviderMeta } from "../config/ssoProviders";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const application_id = envConfig.applicationId;
const isUserRegistrationAllowed = envConfig.isUserRegistrationAllowed;


function SignInPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [lastEmail, setLastEmail] = useState("");

    const [roleGateOpen, setRoleGateOpen] = useState(false);
    const [roleGateName, setRoleGateName] = useState("");

    // SSO error state — separate state pair so D-05 alerts never collide with the
    // existing email-verification alert that reuses alertOpen/alertMessage.
    const [ssoAlertOpen, setSsoAlertOpen] = useState(false);
    const [ssoAlertMessage, setSsoAlertMessage] = useState("");

    // Redirect to dashboard if user is already logged in.
    // We also restore userData from the login/check response so PrivateRoute
    // never sees a missing userData and sends the user back to sign-in.
    React.useEffect(() => {
        let mounted = true;

        (async () => {
            // ASKED FRESH. This answer decides whether to bounce straight back
            // out to ?next=, so a stale "yes" from the few-second cache sends a
            // visitor whose session has just been refused back to the page that
            // refused them — which sends them here again. A cached yes at this
            // one call site is a loop with no exit; the round trip is not.
            const userData = await isUserLoggedIn(undefined, { fresh: true });
            if (!mounted) return;

            if (userData) {
                // Restore userData to localStorage if it was cleared (e.g. page reload)
                // so that PrivateRoute finds it and doesn't redirect back here.
                if (!retrieve("userData")) {
                    const { message, ...userFields } = userData;
                    store("userData", userFields);
                }
                // Honour ?next= for an ALREADY signed-in visitor too. Without
                // this, someone with a live session who follows a
                // /sign-in?next=... link (an action button sending them here to
                // complete a registration) is dropped on the dashboard and the
                // thing they clicked never happens.
                const params = new URLSearchParams(window.location.search);
                const requested = params.get("next");
                const safeNext =
                    requested && requested.startsWith('/') && !requested.startsWith('//')
                        ? requested
                        : "/dashboard";
                navigate(safeNext, { replace: true });
            }
        })();

        return () => { mounted = false; };
    }, [navigate]);

    const nextParam = React.useMemo(() => {
        try {
            const params = new URLSearchParams(location.search);
            const next = params.get("next") || "/dashboard";
            // Only allow relative same-origin paths to prevent open-redirect attacks.
            // Reject anything that starts with '//' (protocol-relative) or contains
            // a scheme (e.g. 'https://evil.com').
            if (!next.startsWith('/') || next.startsWith('//')) return "/dashboard";
            return next;
        } catch (e) {
            return "/dashboard";
        }
    }, [location.search]);

    // D-05/D-06/D-07: Parse sso_error and provider query params set by SsoCallbackPage.js
    // on error redirects (Plan 04). No open-redirect guard needed — these params are used
    // only in exact-string comparisons and as a dictionary lookup key, never as a URL or
    // rendered raw (T-04.1-12/T-04.1-14 per plan threat model).
    const ssoError = React.useMemo(() => {
        try {
            return new URLSearchParams(location.search).get("sso_error") || null;
        } catch (e) {
            return null;
        }
    }, [location.search]);

    const ssoProviderKey = React.useMemo(() => {
        try {
            return new URLSearchParams(location.search).get("provider") || null;
        } catch (e) {
            return null;
        }
    }, [location.search]);

    // Derive provider metadata for D-06/D-07 copy — resolved via the client-side
    // getProviderMeta() map (D-13), never raw-rendered from the query param itself.
    const ssoProviderMeta = React.useMemo(() => {
        if (!ssoProviderKey) return null;
        return getProviderMeta(ssoProviderKey);
    }, [ssoProviderKey]);

    // D-05: Show generic SSO failure banner for any error code other than the
    // "no account found" case (which gets its own inline prompt block).
    // Trigger via useEffect on ssoError to match the query-param-driven UI
    // initialisation pattern used elsewhere in this codebase.
    React.useEffect(() => {
        if (ssoError && ssoError !== "no_account_for_sso_provider") {
            // D-05 literal copy — never reflects the raw error code string (T-04.1-14)
            setSsoAlertMessage("Sign-in with SSO failed, please try again.");
            setSsoAlertOpen(true);
        }
    }, [ssoError]);

    const resendVerificationEmail = async (email) => {
        try {
            const response = await apiHelper.apiCall("/arcee/user/resend-verification/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email , application_id}),
            });
            const data = await response.json();
            return { success: true, message: data.message };
        } catch (error) {
            return {
                success: false,
                message: error.detail?.message || error.detail?.error || "Failed to resend verification email. Please try again."
            };
        }
    };

    const handleSignIn = async ({ username, password }, setErrors) => {
        try {
            const response = await apiHelper.apiCall("/arcee/user/login/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password, application_id }),
            });

            if (!response.ok) {
                const err = await response.json();
                setErrors({ password: err.error || "Login failed" });
                return;
            }

            const data = await response.json();
            console.log("Login successful:", data);

            const { message, ...userData } = data;
            store("userData", userData);
            // Keep a copy of the roles: /arcee/user/login/check does not return
            // them, so without this the organizer guard loses the role on reload.
            storeRoles(userData.roles);

            // Signal NotificationProvider to reinitialise WS and seed state
            // for the newly logged-in user / application.
            window.dispatchEvent(new Event('arcee:auth-change'));

            // An organizer_admin chooses where to land. Skipped when the user
            // followed a real ?next= deep link (honour where they were heading).
            // "/dashboard" is the
            // default destination, and a next= pointing back at /sign-in is a
            // bounce, not a destination — neither counts as a deep link.
            const isDeepLink = nextParam !== "/dashboard" && !nextParam.startsWith("/sign-in");
            if (isOrganizerAdmin(userData) && !isDeepLink) {
                setRoleGateName(userData.first_name || "");
                setRoleGateOpen(true);
                return;
            }

            navigate(nextParam, { replace: true });
        } catch (error) {
            if (error.detail && error.detail.error === "email_not_verified") {
                const email = error.detail.email;
                setLastEmail(email);
                setAlertMessage(
                    "Your email address has not been verified yet. We are sending a new verification link to your inbox. Please check your email (and spam folder)."
                );
                setAlertOpen(true);
                await resendVerificationEmail(email); // reuse same function, no duplication
                return;
            }
            setErrors({ password: error.detail?.error || error.message || "Sign in failed." });
        }
    };

    const handleRoleChoice = (choice) => {
        setRoleGateOpen(false);
        navigate(choice === ROLE_CHOICE_ORGANIZER ? "/organizer/events" : "/feed", { replace: true });
    };

    // D-07: "Sign up instead?" re-triggers the SSO authorize flow with intent=signup.
    // Constructs the same authorize URL shape Plan 03's buildSsoAuthorizeUrl() produces,
    // confirmed via 04.1-03-SUMMARY.md and 04.1-03-PLAN.md <interfaces> block.
    // Was hand-built here, which quietly drifted from the form's builder the
    // moment Apple needed a different redirect_uri. Now the one builder serves
    // all three call sites.
    // const handleSsoSignUpInstead = () => {
    //     if (!ssoProviderKey) return;
    //     const redirectUri = `${window.location.origin}/auth/sso/callback/${ssoProviderKey}`;
    //     const authorizeUrl =
    //         `${envConfig.backendApiUrl}/arcee/sso/${ssoProviderKey}/authorize/` +
    //         `?intent=signup&application_id=${envConfig.applicationId}` +
    //         `&redirect_uri=${encodeURIComponent(redirectUri)}`;
    //     window.location = authorizeUrl;
    // };
    const handleSsoSignUpInstead = () => {
        if (!ssoProviderKey) return;
        // This is the second leg of the same trip: the visitor is still on
        // /sign-in?next=..., so the parameter is still in the URL and has to be
        // stashed again before the SPA is left for a second time.
        stashSsoNext(location.search);
        window.location = buildSsoAuthorizeUrl({
            backendApiUrl: envConfig.backendApiUrl,
            providerKey: ssoProviderKey,
            intent: "signup",
            applicationId: envConfig.applicationId,
        });
    };

    if (!envConfig.isUserLoginAllowed) {
        return <PublicErrorPage error={{ status: 404 }} />;
    }

    return (
        <>
            {/* D-06/D-07: No-account-found prompt — rendered above SignInForm for
                immediate visibility; only shown when sso_error=no_account_for_sso_provider */}
            {ssoError === "no_account_for_sso_provider" && ssoProviderMeta && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 16,
                        background: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: 6,
                    }}
                >
                    <p style={{ margin: 0, marginBottom: 8 }}>
                        {/* D-06 literal copy with {ProviderLabel} resolved via getProviderMeta() */}
                        No account found for this {ssoProviderMeta.label} account. Sign up instead?
                    </p>
                    {/* D-07 action — "Sign up with {ProviderLabel}" label */}
                    <button
                        type="button"
                        onClick={handleSsoSignUpInstead}
                        style={{
                            background: "none",
                            border: "1px solid #ffc107",
                            borderRadius: 4,
                            padding: "4px 12px",
                            cursor: "pointer",
                        }}
                    >
                        Sign up with {ssoProviderMeta.label}
                    </button>
                </div>
            )}
            <SignInForm onSubmit={handleSignIn} showSignUp={isUserRegistrationAllowed} />
            <RoleChoiceDialog
                open={roleGateOpen}
                firstName={roleGateName}
                onChoose={handleRoleChoice}
            />
            <AlertDialog
                open={alertOpen}
                title="Email Verification Required"
                message={alertMessage}
                type="warning"
                okLabel="OK"
                showCancel={false}
                // cancelLabel={resending ? "Sending..." : "Resend Email"}
                onOk={() => setAlertOpen(false)}
                // onCancel={handleResendVerification}
                mini_mode={false}
            />
            {/* D-05: Generic SSO failure banner — separate dialog instance to avoid
                flash/race with the existing email-verification alert above (executor
                decision: use ssoAlertOpen/ssoAlertMessage pair, documented in SUMMARY) */}
            <AlertDialog
                open={ssoAlertOpen}
                title="SSO Sign-in Failed"
                message={ssoAlertMessage}
                type="error"
                okLabel="OK"
                showCancel={false}
                onOk={() => setSsoAlertOpen(false)}
                mini_mode={false}
            />
        </>
    );
}

export default SignInPage;
